import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Sparkles, TrendingUp, User, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

/**
 * RecommendedForYou - Personalized recommendations component
 * 
 * For logged-in users: Shows cards based on viewing history
 * For guests: Shows trending/popular items
 */
export const RecommendedForYou = () => {
  const { api, isAuthenticated, user } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [recommendationType, setRecommendationType] = useState('trending');
  const [basedOn, setBasedOn] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch recommendations
  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setIsLoading(true);
        const response = await api().get('/marketplace/recommendations?count=8');
        setRecommendations(response.data.recommendations || []);
        setRecommendationType(response.data.type || 'trending');
        setBasedOn(response.data.based_on || []);
      } catch (error) {
        console.error('Failed to fetch recommendations:', error);
        // Fallback to trending
        try {
          const trendingResponse = await api().get('/marketplace/trending?count=8');
          setRecommendations(trendingResponse.data.recommendations || []);
          setRecommendationType('trending');
        } catch (fallbackError) {
          console.error('Fallback failed:', fallbackError);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecommendations();
  }, [api, isAuthenticated]);

  // Track listing click
  const trackClick = async (listingId) => {
    try {
      await api().post('/marketplace/track', {
        listing_id: listingId,
        interaction_type: 'click'
      });
    } catch (error) {
      // Silent fail
    }
  };

  // Format tag for display
  const formatTag = (tag) => {
    if (!tag) return '';
    const [type, value] = tag.split(':');
    if (!value) return tag;
    return value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  if (isLoading) {
    return (
      <div className="py-12">
        <div className="flex items-center gap-3 mb-8">
          <User className="w-6 h-6 text-blue-400 animate-pulse" />
          <h2 className="text-2xl font-bold text-white">Recommended for You</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="aspect-[3/4] bg-zinc-800/50 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return null;
  }

  return (
    <div className="py-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          {recommendationType === 'personalized' ? (
            <User className="w-6 h-6 text-blue-400" />
          ) : (
            <TrendingUp className="w-6 h-6 text-amber-400" />
          )}
          <div>
            <h2 className="text-2xl font-bold text-white">
              {recommendationType === 'personalized' 
                ? 'Recommended for You' 
                : 'Trending Now'}
            </h2>
            {recommendationType === 'personalized' && basedOn.length > 0 && (
              <p className="text-xs text-zinc-500 mt-1">
                Based on your interest in {basedOn.slice(0, 3).map(formatTag).join(', ')}
              </p>
            )}
          </div>
        </div>
        
        <Link to="/marketplace">
          <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
            View All
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>

      {/* Listings Grid */}
      <div 
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
        data-testid="recommendations-grid"
      >
        {recommendations.map((listing) => (
          <Link
            key={listing.id}
            to={`/listing/${listing.id}`}
            onClick={() => trackClick(listing.id)}
            className="group"
            data-testid={`recommendation-card-${listing.id}`}
          >
            <div className="bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10">
              {/* Image */}
              <div className="aspect-[3/4] relative overflow-hidden bg-zinc-800">
                <img
                  src={listing.images?.[0] || listing.image_url || '/placeholder-card.png'}
                  alt={listing.player_name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                {/* Grading Badge */}
                {listing.grading_company && listing.grade && (
                  <div className="absolute top-2 left-2 px-2 py-1 rounded bg-black/70 text-xs font-bold text-white">
                    {listing.grading_company} {listing.grade}
                  </div>
                )}
                {/* Personalized indicator */}
                {recommendationType === 'personalized' && (
                  <div className="absolute top-2 right-2 px-2 py-1 rounded bg-blue-500/80 text-xs font-medium text-white flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    For You
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="p-3">
                <h3 className="font-semibold text-white text-sm truncate group-hover:text-blue-400 transition-colors">
                  {listing.player_name}
                </h3>
                <p className="text-xs text-zinc-500 truncate mt-1">
                  {listing.year} {listing.set_name}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-emerald-400 font-bold">
                    ${listing.price?.toLocaleString()}
                  </span>
                  <span className="text-xs text-zinc-600">
                    @{listing.seller_username}
                  </span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Personalization notice */}
      {!isAuthenticated && (
        <div className="mt-6 text-center">
          <p className="text-xs text-zinc-500">
            <Link to="/login" className="text-blue-400 hover:underline">Sign in</Link>
            {' '}for personalized recommendations based on your interests
          </p>
        </div>
      )}
    </div>
  );
};

export default RecommendedForYou;
